import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  Icon,
  SimpleGrid,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { IoMdArrowForward, IoMdCheckmarkCircle } from "react-icons/io";
import { FaHospital, FaShieldAlt, FaClock, FaRocket } from "react-icons/fa";

import { handleCTA } from "../../../../utils";

// Motion components
const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);
const MotionButton = motion(Button);
const MotionHStack = motion(HStack);

// Animations
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

interface CTASectionProps {
  title?: string;
  subtitle?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  primaryButtonAction?: () => void;
  secondaryButtonAction?: () => void;
  bg?: string;
  py?: number;
  showTrustIndicators?: boolean;
  showBenefits?: boolean;
}

function CTASection({
  title = "Ready to Transform Your Healthcare Practice?",
  subtitle = "Join thousands of healthcare professionals who trust our AI solutions to improve patient care and streamline documentation",
  primaryButtonText = "Start Free Trial",
  secondaryButtonText = "Schedule Demo",
  primaryButtonAction,
  secondaryButtonAction,
  bg,
  py = 20,
  showTrustIndicators = true,
  showBenefits = true,
}: CTASectionProps) {
  // Medical-first color palette
  const sectionBg = useColorModeValue("gray.50", "gray.900");
  const backgroundColor = bg || sectionBg;
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textPrimary = useColorModeValue("gray.900", "white");
  const textSecondary = useColorModeValue("gray.600", "gray.300");
  const purplePrimary = "#6366F1";
  const greenSuccess = "#10B981";
  const amberWarning = "#F59E0B";
  const blueInfo = "#3B82F6";

  const handlePrimaryClick = () => {
    if (primaryButtonAction) {
      primaryButtonAction();
    } else {
      handleCTA("signup");
    }
  };

  const handleSecondaryClick = () => {
    if (secondaryButtonAction) {
      secondaryButtonAction();
    } else {
      handleCTA("demo");
    }
  };

  const benefits = [
    {
      icon: FaClock,
      title: "60% Time Saved",
      description: "Focus on patients, not paperwork",
      color: greenSuccess,
    },
    {
      icon: IoMdCheckmarkCircle,
      title: "99.8% Accuracy",
      description: "Medical-grade AI precision",
      color: purplePrimary,
    },
    {
      icon: FaShieldAlt,
      title: "HIPAA Compliant",
      description: "Enterprise security standards",
      color: blueInfo,
    },
    {
      icon: FaHospital,
      title: "500+ Hospitals",
      description: "Trusted by leading institutions",
      color: amberWarning,
    },
  ];

  return (
    <Box
      as="section"
      py={py}
      bg={backgroundColor}
      position="relative"
      overflow="hidden"
      width="100%"
      height="100%"
      display="flex"
      alignItems="center"
    >
      {/* Background Pattern - Only show if not transparent */}
      {backgroundColor == "transparent" && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.03}
          backgroundImage={`url("data:image/svg+xml,%3csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3e%3cg fill='none' fill-rule='evenodd'%3e%3cg fill='%236366F1' fill-opacity='0.1'%3e%3ccircle cx='30' cy='30' r='2'/%3e%3c/g%3e%3c/g%3e%3c/svg%3e")`}
          backgroundSize="60px 60px"
        />
      )}

      <Container maxW="container.xl" position="relative" zIndex={1}>
        <VStack spacing={12}>
          {/* Main CTA Content */}
          <Box textAlign="center" maxW="container.lg" mx="auto">
            {/* <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Badge
                px={4}
                py={2}
                bg={`${purplePrimary}15`}
                color={purplePrimary}
                fontSize="sm"
                fontWeight="600"
                borderRadius="full"
                letterSpacing="wide"
                textTransform="uppercase"
                border={`1px solid ${purplePrimary}30`}
                mb={6}
              >
                Get Started Today
              </Badge>
            </MotionBox> */}

            <MotionHeading
              size="2xl"
              lineHeight="1.2"
              fontWeight="700"
              color={textPrimary}
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              mb={6}
              {...fadeInUp}
            >
              {title}
            </MotionHeading>

            <MotionText
              fontSize={{ base: "lg", md: "xl" }}
              color={textSecondary}
              maxW="container.md"
              mx="auto"
              lineHeight="1.6"
              mb={8}
              {...fadeInUp}
              transition={{ delay: 0.2 }}
            >
              {subtitle}
            </MotionText>

            {/* CTA Buttons */}
            <MotionHStack
              spacing={6}
              justify="center"
              flexWrap="wrap"
              {...fadeInUp}
              transition={{ delay: 0.3 }}
            >
              <MotionButton
                size="lg"
                bg={purplePrimary}
                color="white"
                _hover={{
                  bg: `${purplePrimary}dd`,
                  transform: "translateY(-3px)",
                  boxShadow: "0 15px 35px rgba(99, 102, 241, 0.3)",
                }}
                _active={{
                  transform: "translateY(-1px)",
                }}
                borderRadius="xl"
                px={8}
                py={6}
                fontWeight="700"
                fontSize="lg"
                onClick={handlePrimaryClick}
                transition="all 0.3s ease"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                leftIcon={<FaRocket />}
              >
                {primaryButtonText}
              </MotionButton>

              <MotionButton
                size="lg"
                variant="outline"
                border={`2px solid ${purplePrimary}`}
                color={purplePrimary}
                bg="transparent"
                _hover={{
                  bg: purplePrimary,
                  color: "white",
                  transform: "translateY(-3px)",
                  boxShadow: "0 15px 35px rgba(99, 102, 241, 0.3)",
                }}
                _active={{
                  transform: "translateY(-1px)",
                }}
                borderRadius="xl"
                px={8}
                py={6}
                fontWeight="700"
                fontSize="lg"
                onClick={handleSecondaryClick}
                transition="all 0.3s ease"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                rightIcon={<IoMdArrowForward />}
              >
                {secondaryButtonText}
              </MotionButton>
            </MotionHStack>
          </Box>

          {/* Benefits Grid */}
          {showBenefits && (
            <MotionBox
              w="full"
              variants={staggerContainer}
              initial="initial"
              animate="animate"
            >
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8} width="100%">
                {benefits.map((benefit, idx) => (
                  <MotionBox
                    key={idx}
                    variants={fadeInUp}
                    whileHover={{
                      y: -8,
                      transition: { duration: 0.3 },
                    }}
                  >
                    <Box
                      bg={cardBg}
                      p={6}
                      borderRadius="2xl"
                      boxShadow="0 8px 32px rgba(0, 0, 0, 0.08)"
                      border={`1px solid ${borderColor}`}
                      textAlign="center"
                      transition="all 0.3s ease"
                      _hover={{
                        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.12)",
                        borderColor: benefit.color,
                      }}
                    >
                      {/* Background accent */}
                      <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        height="4px"
                        bgGradient={`linear(to-r, ${benefit.color}, ${benefit.color}80)`}
                        borderRadius="2xl"
                      />

                      <VStack spacing={4}>
                        <Box
                          p={4}
                          borderRadius="xl"
                          bg={`${benefit.color}15`}
                          color={benefit.color}
                          width="fit-content"
                        >
                          <Icon as={benefit.icon} boxSize={6} />
                        </Box>

                        <VStack spacing={2}>
                          <Text
                            fontSize="lg"
                            fontWeight="700"
                            color={textPrimary}
                          >
                            {benefit.title}
                          </Text>
                          <Text fontSize="sm" color={textSecondary}>
                            {benefit.description}
                          </Text>
                        </VStack>
                      </VStack>
                    </Box>
                  </MotionBox>
                ))}
              </SimpleGrid>
            </MotionBox>
          )}

          {/* Trust Indicators */}
          {showTrustIndicators && (
            <MotionBox w="full" {...fadeInUp} transition={{ delay: 0.4 }}>
              <Box
                bg={cardBg}
                borderRadius="2xl"
                p={8}
                border={`1px solid ${borderColor}`}
                boxShadow="0 8px 32px rgba(0, 0, 0, 0.08)"
                textAlign="center"
              >
                <VStack spacing={6}>
                  <Text fontSize="lg" fontWeight="600" color={textSecondary}>
                    Trusted by Healthcare Leaders
                  </Text>

                  <HStack spacing={8} justify="center" flexWrap="wrap">
                    <VStack spacing={2}>
                      <Icon as={FaShieldAlt} boxSize={8} color={greenSuccess} />
                      <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                        Highly Secure
                      </Text>
                      <Text fontSize="xs" color={textSecondary}>
                        Enterprise Security
                      </Text>
                    </VStack>

                    <VStack spacing={2}>
                      <Icon
                        as={IoMdCheckmarkCircle}
                        boxSize={8}
                        color={purplePrimary}
                      />
                      <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                        99.8% Accuracy
                      </Text>
                      <Text fontSize="xs" color={textSecondary}>
                        Medical Grade AI
                      </Text>
                    </VStack>

                    <VStack spacing={2}>
                      <Icon as={FaClock} boxSize={8} color={amberWarning} />
                      <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                        60% Time Saved
                      </Text>
                      <Text fontSize="xs" color={textSecondary}>
                        Clinical Efficiency
                      </Text>
                    </VStack>

                    <VStack spacing={2}>
                      <Icon as={FaHospital} boxSize={8} color={blueInfo} />
                      <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                        500+ Clinicians
                      </Text>
                      <Text fontSize="xs" color={textSecondary}>
                        Trusted Platform
                      </Text>
                    </VStack>
                  </HStack>
                </VStack>
              </Box>
            </MotionBox>
          )}

          {/* Final CTA */}
          <MotionBox
            textAlign="center"
            {...fadeInUp}
            transition={{ delay: 0.5 }}
          >
            <Text fontSize="lg" color={textSecondary} mb={4}>
              No credit card required • 14-day free trial • Cancel anytime
            </Text>
            <Text fontSize="sm" color={textSecondary}>
              Join thousands of healthcare professionals transforming patient
              care
            </Text>
          </MotionBox>
        </VStack>
      </Container>
    </Box>
  );
}

export default CTASection;
